import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, graphBuilderApi } from '../../services/api';
import { getCurrentUser } from '../../utils/roleCheck';
import BuildGraphMenu from './BuildGraphMenu';
import './BuildGraph.css';
import './CopyToEnvironmentPage.css';

function CopyToEnvironmentPage() {
  const { graphId } = useParams();
  const navigate = useNavigate();
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estimate
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);

  // Environments
  const [environments, setEnvironments] = useState([]);
  const [envsLoading, setEnvsLoading] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);

  // Import
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importEnv, setImportEnv] = useState('');
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Stream
  const [streamEnv, setStreamEnv] = useState('');
  const [streamName, setStreamName] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamProgress, setStreamProgress] = useState([]);
  const [streamResult, setStreamResult] = useState(null);

  // Workspaces
  const [workspaces, setWorkspaces] = useState([]);
  const [streamWorkspace, setStreamWorkspace] = useState('');
  const [importWorkspace, setImportWorkspace] = useState('');

  const loadGraphDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getGraphDetails(graphId);
      if (response.success) {
        setGraph(response.graph);
      } else {
        setError('Failed to load graph details');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load graph details');
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    loadGraphDetails();
  }, [loadGraphDetails]);

  // Load estimate + environments on mount
  useEffect(() => {
    if (!graphId) return;

    setEstimateLoading(true);
    graphBuilderApi.graphTransferEstimate(graphId)
      .then(data => setEstimate(data))
      .catch(err => console.error('Estimate failed:', err))
      .finally(() => setEstimateLoading(false));

    setEnvsLoading(true);
    graphBuilderApi.graphTransferEnvironments()
      .then(data => {
        const envs = data.environments || [];
        setEnvironments(envs);
        if (envs.length > 0) {
          setImportEnv(envs[0].name);
          setStreamEnv(envs[0].name);
        }
      })
      .catch(err => console.error('Environments failed:', err))
      .finally(() => setEnvsLoading(false));

    apiService.getWorkspacesForDropdown()
      .then(data => {
        const wsList = data.workspaces || data || [];
        setWorkspaces(wsList);
      })
      .catch(err => console.error('Workspaces failed:', err));
  }, [graphId]);

  // Set default stream name and workspace when graph loads
  useEffect(() => {
    if (graph?.graphName) {
      const now = new Date();
      const ts = String(now.getMonth() + 1).padStart(2, '0')
        + String(now.getDate()).padStart(2, '0')
        + now.getFullYear()
        + String(now.getHours()).padStart(2, '0')
        + String(now.getMinutes()).padStart(2, '0');
      setStreamName(`${graph.graphName}_${ts}`);
    }
    if (graph?.workspaceId) {
      setStreamWorkspace(graph.workspaceId);
      setImportWorkspace(graph.workspaceId);
    }
  }, [graph]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await graphBuilderApi.graphTransferExport(graphId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `graph_${graphId.substring(0, 8)}.ndjson.gz`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportPreview(null);
    setImportResult(null);

    try {
      const preview = await graphBuilderApi.graphTransferPreview(file);
      setImportPreview(preview);
      const manifest = preview.manifest || {};
      const origName = manifest.graph_id ? `imported_graph` : 'imported_graph';
      const now = new Date();
      const ts = String(now.getMonth() + 1).padStart(2, '0')
        + String(now.getDate()).padStart(2, '0')
        + now.getFullYear()
        + String(now.getHours()).padStart(2, '0')
        + String(now.getMinutes()).padStart(2, '0');
      setImportName(`${origName}_${ts}`);
    } catch (err) {
      setError(`Preview failed: ${err.message}`);
    }
  };

  const handleImport = async () => {
    if (!importFile || !importEnv) return;
    setImporting(true);
    setImportResult(null);
    try {
      const user = getCurrentUser();
      const result = await graphBuilderApi.graphTransferImport(importFile, importEnv, importName || undefined, user?.userId, importWorkspace || undefined);
      setImportResult(result);
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleStreamTransfer = async () => {
    if (!streamEnv) return;
    setStreaming(true);
    setStreamProgress([]);
    setStreamResult(null);

    try {
      const user = getCurrentUser();
      await graphBuilderApi.graphTransferStream(
        graphId, streamEnv, streamName || undefined, user?.userId, streamWorkspace || undefined,
        (event) => {
          if (event.stage === 'complete') {
            setStreamResult(event.report);
            setStreaming(false);
          } else if (event.stage === 'error') {
            setError(`Stream transfer failed: ${event.error}`);
            setStreaming(false);
          } else {
            setStreamProgress(prev => [...prev, event]);
          }
        }
      );
    } catch (err) {
      setError(`Stream transfer failed: ${err.message}`);
      setStreaming(false);
    }
  };

  const handleBack = () => {
    navigate(`/user-home/graphs/build/${graphId}`);
  };

  if (loading) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu workspaceId={graph?.workspaceId} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading graph...</p>
        </div>
      </div>
    );
  }

  const nodeCount = estimate?.tables?.Node || 0;
  const synapseCount = estimate?.tables?.Synapses || 0;
  const totalVectors = estimate?.total_vectors || 0;

  return (
    <div className="build-graph-page">
      <BuildGraphMenu workspaceId={graph?.workspaceId} />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Copy to Environment</h1>
          {graph && <p className="page-subtitle">{graph.graphName}</p>}
        </div>
        <div className="header-right">
          <button className="btn-secondary" onClick={handleBack}>
            Back to Build Graph
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, cursor: 'pointer', background: 'none', border: 'none', fontWeight: 600 }}>×</button>
        </div>
      )}

      {/* Graph Summary Card */}
      <div className="cte-card">
        <h3>Graph Summary</h3>
        {estimateLoading ? (
          <p className="cte-muted">Loading estimate...</p>
        ) : estimate ? (
          <div className="cte-stats-grid">
            <div className="cte-stat">
              <span className="cte-stat-value">{nodeCount.toLocaleString()}</span>
              <span className="cte-stat-label">Nodes</span>
            </div>
            <div className="cte-stat">
              <span className="cte-stat-value">{synapseCount.toLocaleString()}</span>
              <span className="cte-stat-label">Synapses</span>
            </div>
            <div className="cte-stat">
              <span className="cte-stat-value">{totalVectors.toLocaleString()}</span>
              <span className="cte-stat-label">Vectors</span>
            </div>
            <div className="cte-stat">
              <span className="cte-stat-value">{estimate.estimated_bundle_mb} MB</span>
              <span className="cte-stat-label">Est. Bundle Size</span>
            </div>
            <div className="cte-stat">
              <span className="cte-stat-value cte-recommended">{estimate.recommended_mode}</span>
              <span className="cte-stat-label">Recommended Mode</span>
            </div>
          </div>
        ) : (
          <p className="cte-muted">Could not estimate graph size</p>
        )}
      </div>

      {/* Export Bundle */}
      <div className="cte-card">
        <h3>Export Bundle</h3>
        <p className="cte-description">
          Download the complete graph (nodes, synapses, embeddings) as a portable .ndjson.gz file.
          {nodeCount > 10000 && (
            <span className="cte-warning"> This graph is large — consider using Direct Transfer instead.</span>
          )}
        </p>
        <button
          className="btn-primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export Graph'}
        </button>
      </div>

      {/* Direct Transfer */}
      <div className="cte-card">
        <h3>Direct Transfer</h3>
        <p className="cte-description">
          Stream the graph directly to a target environment. No intermediate file — handles any graph size.
        </p>

        {environments.length === 0 && !envsLoading ? (
          <p className="cte-warning">
            No environments configured. Set <code>GRAPH_TRANSFER_ENVIRONMENTS</code> env var on the GraphBuilder backend.
          </p>
        ) : (
          <div className="cte-form">
            <div className="cte-form-row">
              <label>Target Environment</label>
              <select value={streamEnv} onChange={e => setStreamEnv(e.target.value)} disabled={streaming}>
                {environments.map(env => (
                  <option key={env.name} value={env.name}>
                    {env.label || env.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="cte-form-row">
              <label>Target Workspace</label>
              <select value={streamWorkspace} onChange={e => setStreamWorkspace(e.target.value)} disabled={streaming}>
                <option value="">-- No Workspace --</option>
                {workspaces.map(ws => (
                  <option key={ws.workspaceId || ws.WorkspaceId} value={ws.workspaceId || ws.WorkspaceId}>
                    {ws.workspaceName || ws.WorkspaceName}
                  </option>
                ))}
              </select>
            </div>
            <div className="cte-form-row">
              <label>Graph Name on Target</label>
              <input
                type="text"
                value={streamName}
                onChange={e => setStreamName(e.target.value)}
                placeholder="Auto-generated with timestamp"
                disabled={streaming}
              />
            </div>
            <button
              className="btn-primary"
              onClick={handleStreamTransfer}
              disabled={streaming || !streamEnv}
            >
              {streaming ? 'Transferring...' : 'Start Transfer'}
            </button>
          </div>
        )}

        {streamProgress.length > 0 && (
          <div className="cte-progress">
            <h4>Progress</h4>
            <div className="cte-progress-items">
              {streamProgress.map((evt, i) => (
                <div key={i} className="cte-progress-item">
                  <span className="cte-progress-stage">{evt.stage}</span>
                  <span className="cte-progress-name">{evt.name}</span>
                  <span className="cte-progress-count">{evt.count?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {streamResult && (
          <div className="cte-result cte-result-success">
            <h4>Transfer Complete</h4>
            <p>New Graph ID: <code>{streamResult.new_graph_id}</code></p>
            <p>New Graph Name: <strong>{streamResult.new_graph_name}</strong></p>
            <p>Rows: {streamResult.total_rows?.toLocaleString()} | Vectors: {streamResult.total_vectors?.toLocaleString()}</p>
            {streamResult.errors?.length > 0 && (
              <div className="cte-errors">
                <strong>Warnings:</strong>
                {streamResult.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Import from Bundle */}
      <div className="cte-card">
        <h3>Import from Bundle</h3>
        <p className="cte-description">
          Upload a previously exported .ndjson.gz file and import it into a target environment.
        </p>

        <div className="cte-form">
          <div className="cte-form-row">
            <label>Bundle File</label>
            <input type="file" accept=".gz,.ndjson.gz" onChange={handleFileSelect} disabled={importing} />
          </div>

          {importPreview && (
            <div className="cte-preview">
              <h4>Bundle Preview</h4>
              <p>Graph ID: <code>{importPreview.manifest?.graph_id}</code></p>
              <p>Exported: {importPreview.manifest?.exported_at}</p>
              <div className="cte-preview-counts">
                {Object.entries(importPreview.record_counts || {}).map(([k, v]) => (
                  <span key={k} className="cte-preview-count">{k}: {v}</span>
                ))}
              </div>
            </div>
          )}

          <div className="cte-form-row">
            <label>Target Environment</label>
            <select value={importEnv} onChange={e => setImportEnv(e.target.value)} disabled={importing}>
              {environments.map(env => (
                <option key={env.name} value={env.name}>
                  {env.label || env.name}
                </option>
              ))}
            </select>
          </div>

          <div className="cte-form-row">
            <label>Target Workspace</label>
            <select value={importWorkspace} onChange={e => setImportWorkspace(e.target.value)} disabled={importing}>
              <option value="">-- No Workspace --</option>
              {workspaces.map(ws => (
                <option key={ws.workspaceId || ws.WorkspaceId} value={ws.workspaceId || ws.WorkspaceId}>
                  {ws.workspaceName || ws.WorkspaceName}
                </option>
              ))}
            </select>
          </div>

          <div className="cte-form-row">
            <label>Graph Name on Target</label>
            <input
              type="text"
              value={importName}
              onChange={e => setImportName(e.target.value)}
              placeholder="Auto-generated with timestamp"
              disabled={importing}
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleImport}
            disabled={importing || !importFile || !importEnv}
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>

        {importResult && (
          <div className={`cte-result ${importResult.status === 'ok' ? 'cte-result-success' : 'cte-result-warning'}`}>
            <h4>Import {importResult.status === 'ok' ? 'Complete' : 'Completed with Warnings'}</h4>
            <p>New Graph ID: <code>{importResult.new_graph_id}</code></p>
            <p>New Graph Name: <strong>{importResult.new_graph_name}</strong></p>
            <p>Rows: {importResult.total_rows?.toLocaleString()} | Vectors: {importResult.total_vectors?.toLocaleString()}</p>
            {importResult.errors?.length > 0 && (
              <div className="cte-errors">
                <strong>Warnings:</strong>
                {importResult.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CopyToEnvironmentPage;
